import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { LicenseLocation } from '../types/licenseLocation';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const LicenseLocationManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingLicenseLocation, setEditingLicenseLocation] = useState<LicenseLocation | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch license locations with server-side filtering and pagination
  const {
    data: licenseLocationsResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'licenseLocations',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/licenseLocation?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('LicenseLocation API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const licenseLocations = licenseLocationsResponse?.data || [];
  const pagination = licenseLocationsResponse?.pagination;

  // Fetch licenses for dropdown
  const { data: licensesData = [] } = useQuery({
    queryKey: ['licenses-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/license');
      console.log('LicenseLocationManagement - Licenses API Response:', response.data);
      const licensesData =
        response.data.data?.data?.map((license: any) => ({
          value: license._id,
          label: `${license.name} (${license.code})`,
        })) || [];
      console.log('LicenseLocationManagement - Licenses for dropdown:', licensesData);
      return licensesData;
    },
  });

  const licenseOptions = licensesData;

  // Create mutation
  const createLicenseLocationMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/licenseLocation', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenseLocations'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'licenseLocation'],
      });
      toast({
        title: 'Success',
        description: 'License Location created successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to create license location';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateLicenseLocationMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(
        `/api/master-data/licenseLocation/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenseLocations'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'licenseLocation'],
      });
      toast({
        title: 'Success',
        description: 'License Location updated successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setEditingLicenseLocation(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to update license location';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation (hard delete)
  const deleteLicenseLocationMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/licenseLocation/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenseLocations'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'licenseLocation'],
      });
      toast({
        title: 'Success',
        description: 'License Location deleted successfully',
        variant: 'default',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete license location',
        variant: 'destructive',
      });
    },
  });

  // Helper function to get license name
  const getLicenseName = (license: any): string => {
    if (!license) return '-';
    if (typeof license === 'object' && license.name) {
      return `${license.name} (${license.code || ''})`;
    }
    // If it's just an ID, try to find the name from licensesData
    const found = licensesData.find((l: any) => l.value === license);
    return found ? found.label : String(license);
  };

  // Table columns configuration
  const columns: TableColumn<LicenseLocation>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (_, licenseLocation) => (
        <div className="space-y-1">
          <div className="font-medium">{licenseLocation.name}</div>
          <div className="text-xs text-muted-foreground font-mono">{licenseLocation.code}</div>
        </div>
      ),
    },
    {
      key: 'license',
      label: 'License',
      sortable: true,
      render: (_, licenseLocation) => (
        <div className="text-sm">
          {getLicenseName(licenseLocation.license)}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, licenseLocation) => (
        <Badge
          variant={licenseLocation.status === 'Active' ? 'default' : 'secondary'}
          className={
            licenseLocation.status === 'Active'
              ? 'bg-green-500 hover:bg-green-600'
              : ''
          }
        >
          {licenseLocation.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (_, licenseLocation) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(licenseLocation.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      placeholder: 'Filter by status',
    },
    {
      key: 'license',
      label: 'License',
      type: 'select',
      options: [
        { value: 'all', label: 'All Licenses' },
        ...licenseOptions,
      ],
      placeholder: 'Filter by license',
    },
  ];

  // Form sections configuration
  const formSections: FormSection[] = [
    {
      title: 'License Location Information',
      description: 'Enter the license location details.',
      fields: [
        {
          name: 'name',
          label: 'Location Name',
          type: 'text',
          required: true,
          placeholder: 'Enter license location name',
          description: 'A unique name for this license location',
          validation: z
            .string()
            .min(2, 'Name must be at least 2 characters')
            .max(200, 'Name cannot exceed 200 characters'),
        },
        {
          name: 'code',
          label: 'Location Code',
          type: 'text',
          required: true,
          placeholder: 'Enter license location code (e.g., "LOC-001")',
          description: 'A unique code for this license location',
          validation: z
            .string()
            .min(2, 'Code must be at least 2 characters')
            .max(50, 'Code cannot exceed 50 characters'),
        },
        {
          name: 'license',
          label: 'License',
          type: 'select',
          required: true,
          placeholder: 'Select a license',
          description: 'The license this location belongs to',
          options: licenseOptions,
          validation: z
            .string()
            .min(1, 'License is required'),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Current status of this license location',
        },
      ],
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingLicenseLocation(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (licenseLocation: LicenseLocation) => {
    // Transform the data for the form - extract license ID if it's an object
    const transformedData = {
      ...licenseLocation,
      license: typeof licenseLocation.license === 'object' 
        ? licenseLocation.license._id 
        : licenseLocation.license,
    };
    setEditingLicenseLocation(transformedData as LicenseLocation);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = async (licenseLocation: LicenseLocation) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete "${licenseLocation.name}"? This action cannot be undone.`
      )
    ) {
      deleteLicenseLocationMutation.mutate(licenseLocation._id);
    }
  };

  const handleSubmit = async (data: any) => {
    // Transform the data
    const transformedData = {
      ...data,
      code: data.code.toUpperCase(),
    };

    if (editingLicenseLocation) {
      updateLicenseLocationMutation.mutate({
        id: editingLicenseLocation._id,
        data: transformedData,
      });
    } else {
      createLicenseLocationMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingLicenseLocation(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'License location data has been refreshed',
      variant: 'default',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/licenseLocation/export?${params.toString()}`
      );

      if (response.data.success) {
        const licenseLocations = response.data.data;

        const exportData = licenseLocations.map((loc: LicenseLocation) => ({
          Name: loc.name,
          Code: loc.code,
          License: getLicenseName(loc.license),
          Status: loc.status,
          'Created At': new Date(loc.createdAt).toLocaleString(),
          'Updated At': new Date(loc.updatedAt).toLocaleString(),
        }));

        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map((row: any) =>
            headers
              .map((header) => {
                const value = row[header];
                if (
                  typeof value === 'string' &&
                  (value.includes(',') || value.includes('"'))
                ) {
                  return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
              })
              .join(',')
          ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `license-locations-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast({
          title: 'Success',
          description: `Exported ${licenseLocations.length} license locations`,
          variant: 'default',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description:
          error.response?.data?.error?.message || 'Failed to export license locations',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            License Location Management
          </h1>
          <p className='text-muted-foreground'>
            Manage license locations linked to licenses
          </p>
        </div>
      </div>

      <MasterDataTable<LicenseLocation>
        title='License Locations'
        description='Manage and configure license location information'
        data={licenseLocations}
        columns={columns}
        isLoading={isLoading}
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search license locations by name or code...'
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingLicenseLocation ? 'Edit License Location' : 'Create License Location'}
        sections={formSections}
        initialData={editingLicenseLocation}
        onSubmit={handleSubmit}
        isLoading={
          createLicenseLocationMutation.isPending || updateLicenseLocationMutation.isPending
        }
        mode={editingLicenseLocation ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default LicenseLocationManagement;


